/**
 * ForecastSkeleton.tsx — Forecast programme F6 (register row 74, spec §3.8):
 * the tab's LOADING state — blank blocks in the shapes the page will take
 * (the hero's three columns, the 2/3 + 1/3 rows, the rep table), so the
 * layout does not jump when the response lands.
 *
 * It renders NO text and NO figure: a "£0" drawn before the read lands is a
 * number the page has not been told (spec §3.8, "never render £0 as a
 * placeholder"). The one accessible statement is the status label. The
 * blocks are klaxiora.css's .k-skeleton — the contract's own fill and pulse.
 */
const Block = ({ h, w, r }: { h: number; w?: number | string; r?: number | string }) => (
  <div className="k-skeleton" style={{ height: h, width: w, ...(r !== undefined ? { borderRadius: r } : {}) }} />
)

const TWO_ONE = { gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)' } as const

export function ForecastSkeleton() {
  return (
    <div className="flex flex-col gap-4" role="status" aria-label="Loading forecast" aria-busy="true" data-forecast-skeleton>
      <div aria-hidden="true" className="flex flex-col gap-4">
        {/* the hero: headline · insights · rings */}
        <div className="k-card" data-skeleton="hero">
          <div className="grid gap-6 items-stretch" style={{ padding: 28, gridTemplateColumns: '1.25fr 1fr 300px' }}>
            <div className="flex flex-col gap-3">
              <Block h={22} w={150} r={11} />
              <Block h={17} w={190} />
              <Block h={76} w="70%" />
              <Block h={15} w="90%" />
              <Block h={30} w={170} r={15} />
              <Block h={10} r={5} />
            </div>
            <div className="flex flex-col gap-3 p-4 bg-surface-panel" style={{ borderRadius: 16 }}>
              <Block h={16} w={130} />
              <Block h={64} r={12} />
              <Block h={64} r={12} />
              <Block h={64} r={12} />
            </div>
            <div className="flex flex-col items-center gap-3 p-4 bg-surface-panel" style={{ borderRadius: 16 }}>
              <Block h={184} w={184} r="50%" />
              <Block h={12} w="100%" />
              <Block h={12} w="100%" />
            </div>
          </div>
        </div>
        {/* suggestions (2/3) beside Categorised share + Attribution (1/3) */}
        <div className="grid gap-4 items-start" style={TWO_ONE} data-skeleton="row">
          <div className="k-card p-5 flex flex-col gap-3"><Block h={18} w={200} /><Block h={36} /><Block h={36} /><Block h={36} /><Block h={36} /></div>
          <div className="flex flex-col gap-4 min-w-0">
            <div className="k-card p-5 flex flex-col gap-3"><Block h={18} w={150} /><Block h={28} w="60%" /><Block h={8} r={4} /></div>
            <div className="k-card p-5 flex flex-col gap-3"><Block h={18} w={120} /><Block h={28} w="80%" /><Block h={8} r={4} /></div>
          </div>
        </div>
        {/* the look-ahead (2/3) beside By forecast category (1/3) */}
        <div className="grid gap-4 items-start" style={TWO_ONE} data-skeleton="row">
          <div className="k-card p-5 flex flex-col gap-3"><Block h={18} w={180} /><Block h={180} /></div>
          <div className="k-card p-5 flex flex-col gap-3"><Block h={18} w={170} /><Block h={30} /><Block h={30} /><Block h={30} /><Block h={30} /><Block h={30} /></div>
        </div>
        {/* By rep */}
        <div className="k-card p-5 flex flex-col gap-3" data-skeleton="table">
          <Block h={18} w={90} /><Block h={32} /><Block h={32} /><Block h={32} /><Block h={32} />
        </div>
      </div>
    </div>
  )
}
